import { useEffect, useState } from 'react';
import { getManagedAccessByUser } from 'api/SupraController';
import { getListingsByUser } from 'api/ListingController';
import buildListingFullAddress from 'utils/buildListingFullAddress';
import { useParams } from 'react-router';
import type { ISupraManagedAccessRecordsByUser } from '@/app-package';
import { message } from '@bb/elements-web';
import { AgentCalendarMenu } from '../types';
import type {
  ShowingCalendarMenuOption,
  ShowingsCalendarPageParams,
} from '../types';

export type ManagedAccessListingFilterOption = {
  id: string;
  address: string;
};

type UseManagedAccessByUserParams = {
  selectedMenuOption: ShowingCalendarMenuOption;
  authId: string;
  listingId?: string;
};

type UseManagedAccessByUserResult = {
  records: ISupraManagedAccessRecordsByUser[];
  setRecords: React.Dispatch<
    React.SetStateAction<ISupraManagedAccessRecordsByUser[]>
  >;
  totalCount: number;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  isLoading: boolean;
  pageSize: number;
  listingFilter: string | null;
  setListingFilter: React.Dispatch<React.SetStateAction<string | null>>;
  hasError: boolean;
  listingFilterOptions: ManagedAccessListingFilterOption[];
};

/**
 * Hook to managed fetching of managed access records for a user to be
 * shown in the managed access drawer on the Showings Calendar page.
 * @param params.selectedMenuOption the menu option selected on the Showings Calendar page
 * @param params.authId the id of the authenticated user to fetch managed access records for
 * @param params.listingId optional id of the listing to filter managed access records by, default to 'viewAll' which shows records for all listings
 */
const useManagedAccessByUser = ({
  selectedMenuOption,
  authId,
  listingId = 'viewAll',
}: UseManagedAccessByUserParams): UseManagedAccessByUserResult => {
  const pageSize = 20; // the set page size to fetch from the API

  const { listingId: routeListingId } = useParams<ShowingsCalendarPageParams>();

  const [page, setPage] = useState(1);
  const [records, setRecords] = useState<ISupraManagedAccessRecordsByUser[]>(
    [],
  );
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [listingFilter, setListingFilter] = useState<string | null>(listingId);
  const [hasError, setHasError] = useState<boolean>(false);
  const [listingFilterOptions, setListingFilterOptions] = useState<
    ManagedAccessListingFilterOption[]
  >([]);

  // Reset data on menu option change
  useEffect(() => {
    setPage(1); // reset page to 1 whenever the menu option changes
    setListingFilter(listingId); // reset listing filter when menu option changes - defaults to 'viewAll' which shows all listings
  }, [selectedMenuOption, listingId]);

  // Fetch the managed access records for this user
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);

      try {
        let source: 'grantee' | 'grantor' | undefined;
        if (selectedMenuOption === AgentCalendarMenu.Buyer) {
          source = 'grantee';
        } else if (selectedMenuOption === AgentCalendarMenu.Listing) {
          source = 'grantor';
        }

        const startDate = new Date().toISOString();

        const response = await getManagedAccessByUser({
          startDate,
          limit: pageSize,
          page,
          source,
          listingId:
            listingFilter && listingFilter !== 'viewAll'
              ? listingFilter
              : undefined,
        });

        setRecords(response.data);

        // if viewing all or a specific listing from the listing calendar page show the updated count
        if (listingFilter === 'viewAll' || routeListingId) {
          setTotalCount(response.totalCount);
        }

        setHasError(false);
      } catch (err) {
        message.error('Failed to load managed access records.');
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [page, selectedMenuOption, pageSize, listingFilter]);

  // Fetch the listings for this user to use in the listing filter dropdown
  useEffect(() => {
    const fetchListings = async () => {
      // the dropdown using this data only displays on the 'Listing' page
      if (selectedMenuOption !== 'Listing') return;

      const listingsRes = await getListingsByUser({
        userId: authId,
        page: 0,
      });
      setListingFilterOptions(
        listingsRes.listings.map(listing => ({
          id: listing.id,
          address: buildListingFullAddress(listing.line1, listing.line2),
        })),
      );
    };
    fetchListings();
  }, [authId, selectedMenuOption]);

  return {
    records,
    setRecords,
    totalCount,
    page,
    setPage,
    isLoading,
    pageSize,
    listingFilter,
    setListingFilter,
    hasError,
    listingFilterOptions,
  };
};

export default useManagedAccessByUser;
